import { handleResponse } from "@twiliointernal/e2-commons-shared-utils";
import {
  ApiResponse,
  GetCsrf,
  GetRequest,
  PostPutRequest,
  ErrorApiResponse,
  ErrorResponseIdConsole,
  WiseOwlIngress,
  DefaultHeaders,
  getOscConfig,
} from "@twiliointernal/e2-commons-shared-definitions";

const { csrfEndpoint } = getOscConfig();

const defaultHeaders = {
  "Content-type": "application/json",
};

/**
 * Abstraction layer to interface with any upstream service
 */
class Client {
  serviceEndpoint: string;
  csrf?: string;
  ingress?: WiseOwlIngress;

  constructor(serviceEndpoint: string, ingress?: WiseOwlIngress) {
    this.serviceEndpoint = serviceEndpoint;
    this.csrf = undefined;
    this.ingress = ingress;
  }

  /**
   * This exists here instead of in the SupportApi client
   * because we hit the Twilio API instead of our own API
   */
  fetchCsrf = async (): Promise<GetCsrf> => {
    const response = await fetch(csrfEndpoint, {
      credentials: "include",
    });
    const jsonResponse = await response.json();

    return jsonResponse;
  };

  get = async <Type>({
    path,
    headers,
    requestOptions,
  }: GetRequest): Promise<ApiResponse<Type>> => {
    const requestHeaders = new Headers(headers);
    const response = await fetch(`${this.serviceEndpoint}${path}`, {
      method: "GET",
      headers: requestHeaders,
      ...requestOptions,
    });

    return handleResponse(response);
  };

  post = async <Type>({
    path,
    body,
    headers,
    requestOptions,
  }: PostPutRequest): Promise<ApiResponse<Type>> => {
    const requestHeaders = new Headers(headers);
    const response = await fetch(`${this.serviceEndpoint}${path}`, {
      method: "POST",
      headers: requestHeaders,
      body,
      ...requestOptions,
    });

    return handleResponse(response);
  };

  put = async <Type>({
    path,
    body,
    headers,
    requestOptions,
  }: PostPutRequest): Promise<ApiResponse<Type>> => {
    const requestHeaders = new Headers(headers);
    const response = await fetch(`${this.serviceEndpoint}${path}`, {
      method: "PUT",
      headers: requestHeaders,
      body,
      ...requestOptions,
    });

    return handleResponse(response);
  };

  patch = async <Type>({
    path,
    body,
    headers,
    requestOptions,
  }: PostPutRequest): Promise<ApiResponse<Type>> => {
    const requestHeaders = new Headers(headers);
    const response = await fetch(`${this.serviceEndpoint}${path}`, {
      method: "PATCH",
      headers: requestHeaders,
      body,
      ...requestOptions,
    });

    return handleResponse(response);
  };

  shouldLogWarning = (
    response: ErrorApiResponse | Response | ErrorResponseIdConsole
  ): boolean => {
    const statusToEvaluate = (response as ErrorApiResponse).statusCode
      ? (response as ErrorApiResponse).statusCode
      : (response as Response).status;

    return statusToEvaluate >= 400 && statusToEvaluate < 500;
  };

  getCsrf = async (): Promise<string> => {
    return this.csrf || (await this.fetchCsrf()).csrf;
  };

  setCsrf = (csrf: string): void => {
    this.csrf = csrf;
  };

  protected ingressCanHitOTK = (): boolean => {
    return (
      this.ingress === WiseOwlIngress.zendesk ||
      this.ingress === WiseOwlIngress.salesforce ||
      this.ingress === WiseOwlIngress.wiseOwlApp ||
      this.ingress === WiseOwlIngress.sendGrid
    );
  };

  protected getHeadersWithCsrf = async (): Promise<DefaultHeaders> => {
    const csrf = await this.getCsrf();

    return {
      ...defaultHeaders,
      "x-twilio-csrf": csrf,
    };
  };

  protected getRequestOptions = async (
    extraHeaders: DefaultHeaders = {}
  ): Promise<{
    headers: DefaultHeaders;
    requestOptions?: RequestInit;
  }> => {
    const canAccessOtk = this.ingressCanHitOTK();

    if (canAccessOtk) {
      return {
        headers: {
          ...defaultHeaders,
          // Optionally overwrite default headers with extra headers
          ...extraHeaders,
        },
      };
    }

    const csrfHeaders = await this.getHeadersWithCsrf();
    return {
      headers: {
        ...csrfHeaders,
        // Optionally overwrite default headers with extra headers
        ...extraHeaders,
      },
      requestOptions: { credentials: "include" },
    };
  };
}

export default Client;
